import { useCallback, useEffect, useState } from 'react';

import {
  alpha,
  Box,
  Button,
  Grid,
  IconButton,
  Link,
  List,
  makeStyles,
  Paper,
  Theme,
  Zoom,
} from '@material-ui/core';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import clsx from 'clsx';
import Image from 'material-ui-image';

import ImageViewer from 'components/ImageViewer';
import InfiniteScroll from 'components/InfiniteScroll';
import * as socketEvents from 'constants/socketEvents';
import socket from 'services/socket';
import { useAppDispatch, useAppSelector } from 'store';
import {
  AtualizarMidiaMensagemInterface,
  atualizarMidiasAtendimento,
  atualizarMidiasGrupo,
  baixandoMensagemMidiasAtendimento,
  baixandoMensagemMidiasGrupo,
  BaixandoMidiaMensagemInterface,
  fetchMidiasAtendimento,
  fetchMidiasGrupo,
  MensagemInterface,
  mensagensSelector,
  ParamsPage,
  resetGalerias,
} from 'store/reducers/mensagens';

import ButtonDownloadMidia from './ButtonDownloadMidia';

type TabPanelMidiasProps = {
  atendimentoId?: string;
  grupoChatId?: string;
  isGroup?: boolean;
  midiaItemHeight?: number;
  midiaItemWidth?: number;
};

const useStyles = makeStyles<Theme, StylesProps>(
  ({ spacing, shadows, palette }) => ({
    list: {
      height: '100%',
      overflow: 'auto',
    },
    content: {
      overflow: 'hidden',
      height: '100%',
      paddingLeft: spacing(2),
      paddingBottom: spacing(2),
    },
    paper: {
      display: 'flex',
      position: 'relative',
      alignItems: 'center',
      justifyContent: 'center',
      padding: spacing(1) / 2,
      backgroundColor: palette.background.tertiary,

      '&:hover': {
        boxShadow: shadows[6],
      },
    },
    activeMidia: {
      backgroundColor: palette.primary.main,
    },
    contentItem: {
      width: '100%',
      overflow: 'hidden',
      position: 'relative',
      minWidth: ({ midiaItemHeight }) => midiaItemHeight || spacing(10),
      maxWidth: ({ midiaItemWidth }) => midiaItemWidth || spacing(10),
    },
    image: {
      width: '100%',
      borderRadius: spacing(1),
    },
    iconButton: {
      color: palette.getContrastText(alpha(palette.common.black, 0.4)),
      backgroundColor: `${alpha(palette.common.black, 0.4)} !important`,
      position: 'absolute',
      top: '35%',
      left: '35%',
    },
    background: {
      borderRadius: spacing(1),
      color: palette.common.white,
      backgroundColor: alpha(palette.common.black, 0.68),
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      position: 'absolute',
      bottom: 0,
      top: 0,
      left: 0,
      right: 0,
    },
  }),
);

type StylesProps = {
  midiaItemHeight?: number;
  midiaItemWidth?: number;
};

export default function TabPanelMidias(props: TabPanelMidiasProps) {
  const {
    atendimentoId,
    grupoChatId,
    isGroup = false,
    midiaItemHeight,
    midiaItemWidth,
  } = props;
  const classes = useStyles({ midiaItemHeight, midiaItemWidth });
  const dispatch = useAppDispatch();
  const {
    // Midias atendimento
    midiasAtendimento,
    midiasAtendimentoInfoPage,
    loadingMidiasAtendimento,
    // Midias grupo
    midiasGrupo,
    midiasGrupoInfoPage,
    loadingMidiasGrupo,
  } = useAppSelector(mensagensSelector);
  const [midiaActive, setMidiaActive] = useState<MensagemInterface>();
  const [initialParamsPage] = useState<ParamsPage>({ page: 1, limit: 50 });

  // Lista de mídias do [atendimento]
  const getImagensAtendimento = useCallback(
    (params: { atendimentoId: string; params: ParamsPage }) => {
      dispatch(fetchMidiasAtendimento(params));
    },
    [dispatch],
  );

  // Lista de midias do [grupo]
  const getImagensGrupo = useCallback(
    (params: { grupoChatId: string; params: ParamsPage }) => {
      dispatch(fetchMidiasGrupo(params));
    },
    [dispatch],
  );

  useEffect(() => {
    if (!isGroup && !grupoChatId && atendimentoId) {
      getImagensAtendimento({ atendimentoId, params: initialParamsPage });
    } else if (grupoChatId) {
      getImagensGrupo({ grupoChatId, params: initialParamsPage });
    }

    return () => {
      dispatch(resetGalerias());
    };
  }, [
    atendimentoId,
    dispatch,
    getImagensAtendimento,
    getImagensGrupo,
    grupoChatId,
    initialParamsPage,
    isGroup,
  ]);

  // Carrega as próximas mídias do [atendimento]
  const fetchNextMidiasAtendimento = () => {
    const { nextPage } = midiasAtendimentoInfoPage;
    const checkNextPage = nextPage !== null && nextPage !== undefined;
    if (!loadingMidiasAtendimento && checkNextPage && atendimentoId) {
      const params: ParamsPage = { page: nextPage, limit: 50 };
      getImagensAtendimento({ atendimentoId, params });
    }
  };

  // Carrega as próximas mídias do [grupo]
  const fetchNextMidiasGrupo = () => {
    const { nextPage } = midiasGrupoInfoPage;
    const checkNextPage = nextPage !== null && nextPage !== undefined;
    if (!loadingMidiasGrupo && checkNextPage && grupoChatId) {
      const params: ParamsPage = { page: nextPage, limit: 50 };
      getImagensGrupo({ grupoChatId, params });
    }
  };

  useEffect(() => {
    const onBaixandoMidiaMensagem = (data: BaixandoMidiaMensagemInterface) => {
      if (atendimentoId) dispatch(baixandoMensagemMidiasAtendimento(data));
    };

    const onAtualizarMidiaMensagem = (
      data: AtualizarMidiaMensagemInterface,
    ) => {
      if (atendimentoId) dispatch(atualizarMidiasAtendimento(data));
    };

    socket.on(socketEvents.ON_BAIXANDO_MIDIA_MENSAGEM, onBaixandoMidiaMensagem);
    socket.on(
      socketEvents.ON_ATUALIZAR_MIDIA_MENSAGEM,
      onAtualizarMidiaMensagem,
    );

    return () => {
      socket.off(
        socketEvents.ON_BAIXANDO_MIDIA_MENSAGEM,
        onBaixandoMidiaMensagem,
      );
      socket.off(
        socketEvents.ON_ATUALIZAR_MIDIA_MENSAGEM,
        onAtualizarMidiaMensagem,
      );
    };
  }, [atendimentoId, dispatch, grupoChatId]);

  useEffect(() => {
    const baixandoMidiaMensagemGrupo = (
      data: BaixandoMidiaMensagemInterface,
    ) => {
      if (grupoChatId) dispatch(baixandoMensagemMidiasGrupo(data));
    };

    const atualizarMidiaMensagemGrupo = (
      data: AtualizarMidiaMensagemInterface,
    ) => {
      if (grupoChatId) dispatch(atualizarMidiasGrupo(data));
    };

    socket.on(
      socketEvents.ON_BAIXANDO_MIDIA_MENSAGEM_GRUPO,
      baixandoMidiaMensagemGrupo,
    );
    socket.on(
      socketEvents.ON_ATUALIZAR_MIDIA_MENSAGEM_GRUPO,
      atualizarMidiaMensagemGrupo,
    );

    return () => {
      socket.off(
        socketEvents.ON_BAIXANDO_MIDIA_MENSAGEM_GRUPO,
        baixandoMidiaMensagemGrupo,
      );
      socket.off(
        socketEvents.ON_ATUALIZAR_MIDIA_MENSAGEM_GRUPO,
        atualizarMidiaMensagemGrupo,
      );
    };
  }, [dispatch, grupoChatId]);

  const renderMidia = (midia: MensagemInterface) => {
    const {
      id,
      atendimentoId,
      grupoChatId,
      midiaUrl,
      midiaTitulo,
      midiaDownloadErro,
      midiaUrlPrevia,
      baixando,
    } = midia;
    const type = midia.tipo;
    switch (type) {
      case 'IMAGEM':
        return (
          <Zoom in mountOnEnter unmountOnExit timeout={400}>
            <Paper
              onClick={() => setMidiaActive(midia)}
              elevation={1}
              className={clsx(classes.paper, {
                [classes.activeMidia]:
                  midiaActive?.id === id && !midiaDownloadErro,
              })}
            >
              <Box className={classes.contentItem}>
                <ImageViewer src={midiaUrl} disabled={midiaDownloadErro}>
                  <Image
                    cover
                    loading={false}
                    src={midiaUrl || midiaUrlPrevia || ''}
                    className={classes.image}
                    style={{ backgroundColor: 'inherit' }}
                  />
                </ImageViewer>
                {midiaDownloadErro && (
                  <Box className={classes.background}>
                    <ButtonDownloadMidia
                      buttonText="Recarregar mídia"
                      mensagemId={id}
                      atendimentoId={atendimentoId}
                      grupoChatId={grupoChatId}
                      baixando={baixando}
                    />
                  </Box>
                )}
              </Box>
            </Paper>
          </Zoom>
        );
      case 'VIDEO':
        return (
          <Zoom in mountOnEnter unmountOnExit timeout={400}>
            <Paper
              onClick={() => setMidiaActive(midia)}
              elevation={1}
              className={clsx(classes.paper, {
                [classes.activeMidia]:
                  midiaActive?.id === id && !midiaDownloadErro,
              })}
            >
              <Link
                disabled={midiaDownloadErro}
                component={Button}
                style={{ display: 'flex', padding: 0 }}
                key={id}
                rel="noopener noreferrer"
                title={midiaTitulo}
                target="_blank"
                href={midiaUrl}
              >
                <Box className={classes.contentItem}>
                  <Image
                    className={classes.image}
                    src={midiaUrlPrevia || ''}
                    alt={midiaTitulo || ''}
                    loading={false}
                    style={{ backgroundColor: 'inherit' }}
                  />
                  {!midiaDownloadErro && (
                    <IconButton size="small" className={classes.iconButton}>
                      <PlayArrowIcon color="inherit" />
                    </IconButton>
                  )}
                </Box>
              </Link>
              {midia.midiaDownloadErro && (
                <Box className={classes.background}>
                  <ButtonDownloadMidia
                    buttonText="Recarregar mídia"
                    mensagemId={id}
                    atendimentoId={atendimentoId}
                    grupoChatId={grupoChatId}
                    baixando={baixando}
                  />
                </Box>
              )}
            </Paper>
          </Zoom>
        );
      default:
        return null;
    }
  };

  const renderMidiasAtendimento = () => (
    <List id="scrollableMidiasAtendimento" className={classes.list}>
      <InfiniteScroll
        scrollableTarget="scrollableMidiasAtendimento"
        inverse={false}
        data={midiasAtendimento}
        hasMore={Boolean(midiasAtendimentoInfoPage.nextPage)}
        loading={loadingMidiasAtendimento}
        next={fetchNextMidiasAtendimento}
        empty={{
          title: 'Não há mídias',
          description: 'Nenhuma mídia foi encontrada neste atendimento!',
        }}
      >
        <div className={classes.content}>
          <Grid container spacing={2}>
            {midiasAtendimento.map(midia => (
              <Grid key={midia.id} item>
                {renderMidia(midia)}
              </Grid>
            ))}
          </Grid>
        </div>
      </InfiniteScroll>
    </List>
  );

  const renderMidiasGrupo = () => {
    return (
      <List id="scrollableMidiasGrupo" className={classes.list}>
        <InfiniteScroll
          scrollableTarget="scrollableMidiasGrupo"
          inverse={false}
          data={midiasGrupo}
          hasMore={Boolean(midiasGrupoInfoPage.nextPage)}
          loading={loadingMidiasGrupo}
          next={fetchNextMidiasGrupo}
          empty={{
            title: 'Não há mídias',
            description: 'Nenhuma mídia foi encontrada neste grupo!',
          }}
        >
          <div className={classes.content}>
            <Grid container spacing={2}>
              {midiasGrupo.map(midia => (
                <Grid key={midia.id} item>
                  {renderMidia(midia)}
                </Grid>
              ))}
            </Grid>
          </div>
        </InfiniteScroll>
      </List>
    );
  };

  return !isGroup ? renderMidiasAtendimento() : renderMidiasGrupo();
}
